import { Card, Spin, Typography } from "antd";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Questionnaire,
  QuestionnaireType,
} from "../../../../../../../api/graphql/schema.generated";
import colors from "../../../../../../../themes/colors";
import QuestionnaireDetailedStatus from "../../QuestionnaireDetailedStatus";
import { QuestionnaireInfo } from "./constants";
import { getQuestionnaireDescription } from "./questionnaireEntries";
import QuestionnaireStatus from "./QuestionnaireStatus";

const { Paragraph, Text } = Typography;

export interface QuestionnaireCardProps {
  taxYear: number;
  info: QuestionnaireInfo;
  loading?: boolean;
  detailedStatus?: boolean;
  questionnaire?: Questionnaire;
  isNotRelevant?: boolean;
}

const QuestionnaireCard = ({
  taxYear,
  info,
  questionnaire,
  isNotRelevant,
  detailedStatus = false,
  loading,
}: QuestionnaireCardProps) => {
  const { t } = useTranslation("TaxCaseOverview", {
    keyPrefix: "questionnaireStatus",
  });
  const navigate = useNavigate();
  const location = useLocation();

  const description = useMemo(
    () => getQuestionnaireDescription(info, questionnaire),
    [info, questionnaire]
  );

  const goToQuestionnaire = useCallback(() => {
    if (!info.notAvailable) {
      let pathname = `/tax-declaration/tax-preparation/${info.type}`;

      if (questionnaire?.type === QuestionnaireType.EOY_INCOME_TAX_CHILD) {
        pathname += `/${questionnaire.id}`;
      }

      navigate(
        { pathname, search: location.search },
        {
          state: { key: info.type },
        }
      );
    }
  }, [
    navigate,
    location.search,
    info.notAvailable,
    info.type,
    questionnaire?.id,
    questionnaire?.type,
  ]);

  return (
    <Card
      key={info.type}
      title={`${info.title} ${taxYear}${info.notAvailable ? " (soon)" : ""}`}
      size="small"
      hoverable
      onClick={goToQuestionnaire}
      style={info.notAvailable ? { cursor: "not-allowed" } : {}}
    >
      <Paragraph>{description}</Paragraph>
      {loading ? (
        <Spin size="small" style={{ height: 16 }} />
      ) : isNotRelevant ? (
        <Text style={{ color: colors.darkLimeGreen }}>{t("notRelevant")}</Text>
      ) : detailedStatus ? (
        <QuestionnaireDetailedStatus questionnaire={questionnaire} />
      ) : (
        <QuestionnaireStatus status={questionnaire?.status} />
      )}
    </Card>
  );
};

export default QuestionnaireCard;
